// @ts-check
import Api from '../../apis/Api';
import PointsFilter from '../../objects/pointsFilter/PointsFilter';
import PointsFilterRule from '../../objects/pointsFilter/PointsFilterRule';
import BaseHandler from './BaseHandler';
import { UNCLUSTERED_POINTS_MIN_ZOOM } from '../data/GeoJsonLayersData';
import Mixpanel, { MIXPANEL_EVENTS, PIN_CONFIG_VIEW_TRIGGER } from '../../helpers/Mixpanel';
import FacilityPinConfig, {
    PIN_CONFIG_LABELS,
    LISTING_STATUS_ICON_IMAGE_EXPRESSION,
    PRICING_ICON_IMAGE_EXPRESSION,
    BY_TYPE_ICON_IMAGE_EXPRESSION,
    BY_TYPE_HIGHLIGHT_FILTER_EXPRESSIONS,
    byTypeIconImageWithHighlight,
} from '../../enums/FacilityPinConfig';

class PointsHandler extends BaseHandler {
    constructor(mapViewer) {
        super(mapViewer);
        this._data = mapViewer.dragonflyMapData.geoJsonLayersData;
        this._isMapLoaded = this.map.loaded();

        this.setGluBusEvents({
            MAP_LOAD: this.onMapLoad,
            POINTS_GEOJSON: this.onPointsGeoJsonLoaded,
            MAP_APPLY_GEO_JSON_LIBRARY_LAYERS_UPDATE: this.applyGeoJSONLibraryLayersUpdate,
            MAP_APPLY_GEO_JSON_CLUSTERING_TOGGLE: this.applyGeoJSONClusteringToggle,
            MAP_APPLY_GEO_JSON_LAYER_UPDATE: this.applyGeoJsonLayerUpdate,
            MAP_APPLY_FACILITY_PIN_CONFIG: this.applyFacilityPinConfig,
            MAP_APPLY_HIGHLIGHT: this.applyHighlightFromEvent,
            SHOULD_USE_CLUSTERS_REQUEST: this.onShouldUseClustersRequest,
            APPLIED_FILTERS_RESPONSE: this.onApplyFilters,
        });

        /** @type {import('mapbox-gl').Popup} */
        this._popup = undefined;
        this._layerVisibility = undefined;
        this._featurePropertyNameEquivalentToLayerId = undefined;
        this._filterCombinerType = undefined;
        this._appliedFilters = undefined;
        this._shouldUseClusters = false;
        // Classic (LEGACY) category teardrops are the baseline; the map instance
        // carries the user's restored choice when there is one.
        this._facilityPinConfig = this.mapInstance.facilityPinConfig || FacilityPinConfig.LEGACY;
        // Same as the pin config and the deal pins: the restored eye-icon state rides
        // on the map instance, so it survives every map-viewer rebuild.
        this._facilitiesVisible = this.mapInstance.facilityPinsVisible !== false;
        // One-time guard for the initial-load adoption impression (see onUserInfo).
        this._initialPinConfigTracked = false;
        /** @type {Promise<Set<string>>|undefined} */
        this._listedFacilityIdsPromise = undefined;
        // Highlight state for the "by type" variant config. Defaults to no
        // highlight (null metric), matching the legend's "None" default
        // (GeoJsonLibraryLegend) so there's a single source of truth for the
        // initial state — the first chip selection sets the metric.
        this._highlightVisible = false;
        this._highlightMetric = null;
        this.bus.once('GEO_JSON_METADATA', this.onGeoJsonMetadataRetrieved);
        this.emit('GEO_JSON_METADATA_REQUEST', { source: this });
        this.bus.once('USER_INFO_GET_SUCCESS', this.onUserInfo);
        this.emit('USER_INFO_GET_REQUEST', { source: this });
    }

    onMapLoad = e => {
        if (e.source.id !== this.mapInstanceId) return;
        this._isMapLoaded = true;
    };

    /**
     * The default pin mode is LEGACY for everyone (set in the constructor), so there
     * is no per-tier promotion to do here anymore. We still wait for user info to
     * emit the one-time load-time adoption impression (so the event carries the same
     * session/user context as the rest of the map's telemetry).
     */
    onUserInfo = (userInfo, source) => {
        if (source && this !== source) {
            return;
        }
        // Emit the initial (default = LEGACY) pin mode once per load so adoption
        // share counts users who never open the editor.
        this._trackInitialPinConfig();
    };

    /**
     * Fires a one-time Mixpanel impression of the active pin mode on initial load.
     * This is the load-time trigger:'load' counterpart of the on-toggle trigger:'user_toggle'
     * emit (GeoJsonLibraryLegend#onSelectPinConfig) — both use VIEW_FACILITY_PIN_CONFIG —
     * so adoption share also counts users who never open the editor.
     */
    _trackInitialPinConfig() {
        if (this._initialPinConfigTracked) return;
        this._initialPinConfigTracked = true;
        const pinConfig = this._facilityPinConfig;
        Mixpanel.emitUserEvent(MIXPANEL_EVENTS.VIEW_FACILITY_PIN_CONFIG, {
            pinConfig,
            label: PIN_CONFIG_LABELS[pinConfig],
            trigger: PIN_CONFIG_VIEW_TRIGGER.LOAD,
        });
    }

    /**
     * Fetches the set of facility ids the user can see as listed on the
     * platform, memoizing the in-flight/resolved promise so repeated calls
     * within a single activation (e.g. geojson reloads on pan/zoom) reuse one
     * request. The memo is intentionally NOT session-lived: it is reset every
     * time "by listing status" is (re)activated (see applyFacilityPinConfig) so
     * publish/unpublish/close changes made mid-session are reflected without a
     * full reload. The endpoint returns an ETag + `must-revalidate`, so an
     * unchanged set comes back as a cheap 304 (STAR-1895).
     * Used by the "by listing status" pin config to flag features with
     * `has_listing`.
     * @returns {Promise<Set<string>>}
     */
    fetchListedFacilityIds() {
        if (!this._listedFacilityIdsPromise) {
            this._listedFacilityIdsPromise = Api.listings
                .mapFacilityIds()
                .then(response => {
                    const ids =
                        response && response.body && response.body.facility_ids
                            ? response.body.facility_ids
                            : [];
                    return new Set(ids.map(id => String(id)));
                })
                .catch(() => new Set());
        }
        return this._listedFacilityIdsPromise;
    }

    /**
     * Switches the facility pins between the default "by pricing" scheme and the
     * "by listing status" scheme. For listing-status, listed facility ids are
     * fetched and merged onto the features as `has_listing`, then the symbol
     * layers' icon-image expression is swapped. Switching back restores the
     * original icon-image.
     *
     * The event may carry `layerVisibility` + `featurePropertyNameEquivalentToLayerId`
     * so the legend's per-row show/hide filter switches atomically with the
     * config (listing-status rows filter on the derived virtual-layer property,
     * the default legend on `category`).
     */
    applyFacilityPinConfig = e => {
        const { config } = e;
        this._facilityPinConfig = config;

        if (config === FacilityPinConfig.LISTING_STATUS) {
            // Re-activating listing-status mode: drop any cached set so the ids
            // re-fetch (reflecting publish/unpublish/close changes made since the
            // last activation). Within this activation the memo is reused, so
            // subsequent geojson reloads on pan/zoom don't refetch (STAR-1895).
            this._listedFacilityIdsPromise = undefined;
            this.fetchListedFacilityIds().then(listedIds => {
                this._data.mergeFeatureListingFlags(listedIds);
                this._data.setFacilityPinIconImage(LISTING_STATUS_ICON_IMAGE_EXPRESSION);
                this.setLocalsFromGeoJsonLayerUpdateParams(e);
                this.refreshPointsLayers();
            });
        } else if (config === FacilityPinConfig.BY_TYPE) {
            // "By type" variant: operating = green circle, development = yellow
            // square, regardless of listing status or pricing. We still fetch
            // listed ids so `has_listing`/`on_market` are normalized on every
            // feature (the highlight filter reads them).
            const highlightMetric = e.byTypeHighlightMetric || null;
            // Keep internal highlight state in sync for the standalone
            // MAP_APPLY_HIGHLIGHT path (live chip clicks while already in Beta).
            this._highlightVisible = Boolean(highlightMetric);
            if (highlightMetric) {
                this._highlightMetric = highlightMetric;
            }
            this._listedFacilityIdsPromise = undefined;
            this.fetchListedFacilityIds().then(listedIds => {
                this._data.mergeFeatureListingFlags(listedIds);
                // Classify features for the legend's highlighted/non-highlighted rows.
                // Runs after mergeFeatureListingFlags (the predicates read its flags) and
                // before refreshPointsLayers (which filters on the derived property).
                this._data.mergeByTypeLayerIds(highlightMetric);
                if (highlightMetric) {
                    const highlightFilter = BY_TYPE_HIGHLIGHT_FILTER_EXPRESSIONS[highlightMetric];
                    this._data.setFacilityPinIconImage(
                        byTypeIconImageWithHighlight(highlightFilter),
                    );
                } else {
                    this._data.setFacilityPinIconImage(BY_TYPE_ICON_IMAGE_EXPRESSION);
                }
                this.setLocalsFromGeoJsonLayerUpdateParams(e);
                this.refreshPointsLayers();
            });
        } else if (config === FacilityPinConfig.LEGACY) {
            // "Classic" arm: restore the original category-based teardrop pins that
            // ship in the points metadata. setFacilityPinIconImage(null) reinstates
            // the captured _originalIconImages, so no new expression is needed.
            // `category` is already on every feature — no listed-id fetch/merge.
            this._data.setFacilityPinIconImage(null);
            this.setLocalsFromGeoJsonLayerUpdateParams(e);
            this.refreshPointsLayers();
        } else {
            // "By pricing": color from `category` (already on each feature), no
            // listed-id fetch required.
            this._data.setFacilityPinIconImage(PRICING_ICON_IMAGE_EXPRESSION);
            this.setLocalsFromGeoJsonLayerUpdateParams(e);
            this.refreshPointsLayers();
        }
    };

    /**
     * Switches highlighted pins to the orange-outline sprite variant based on
     * the selected metric filter. The outline is baked into the SVG via an
     * icon-image expression swap (no separate overlay layer).
     * @param {boolean} visible
     * @param {string} metric — one of BY_TYPE_HIGHLIGHT_METRICS
     */
    applyHighlight(visible, metric) {
        this._highlightVisible = visible;
        this._highlightMetric = metric;

        if (!this._isMapLoaded) return;

        // The metric defines the highlighted/non-highlighted split, so re-classify before
        // refreshing — otherwise the legend rows would filter on the previous metric.
        this._data.mergeByTypeLayerIds(this.activeHighlightMetric);

        if (!visible) {
            this._data.setFacilityPinIconImage(BY_TYPE_ICON_IMAGE_EXPRESSION);
            this.refreshPointsLayers();
            return;
        }

        const highlightFilter = BY_TYPE_HIGHLIGHT_FILTER_EXPRESSIONS[metric];
        this._data.setFacilityPinIconImage(byTypeIconImageWithHighlight(highlightFilter));
        this.refreshPointsLayers();
    }

    /**
     * The metric actually in effect. `_highlightMetric` intentionally remembers the last
     * selection after the highlight is cleared (so `applyHighlightFromEvent` can restore
     * it), so "no highlight" has to be read off `_highlightVisible`.
     * @returns {string|null}
     */
    get activeHighlightMetric() {
        return this._highlightVisible ? this._highlightMetric : null;
    }

    /**
     * Bus event handler for MAP_APPLY_HIGHLIGHT, emitted by the legend
     * toggle. Payload: `{ highlightVisible: boolean, metric: string }`, optionally with
     * `layerVisibility` + `featurePropertyNameEquivalentToLayerId` — the metric change
     * redefines the highlighted/non-highlighted rows, so the legend sends the reconciled
     * visibility along and both land in one re-render.
     */
    applyHighlightFromEvent = e => {
        const { highlightVisible, metric } = e;
        this.setLocalsFromGeoJsonLayerUpdateParams(e);
        this.applyHighlight(highlightVisible, metric || this._highlightMetric);
    };

    // Re-renders the points layers (source data + layers) so a changed
    // icon-image expression or merged feature properties take effect. Reuses the
    // existing update path, which preserves the current legend/filter state.
    refreshPointsLayers() {
        this.applyGeoJsonLayerUpdate({});
    }

    /**
     * @param {import('../../').PointsMetadata} geoJsonMetadata
     * @param target
     */
    onGeoJsonMetadataRetrieved = (geoJsonMetadata, target) => {
        if (target === this && geoJsonMetadata) {
            this._data.setGeoJsonLayersData(geoJsonMetadata);
            // Apply the active config's icon-image up front. Pricing uses the 1.2
            // two-color scheme (Has Pricing / No Pricing) reading `category`;
            // listing status and by-type use their own expressions and get their
            // `has_listing`/`on_market` flags merged once points load
            // (onPointsGeoJsonLoaded). Either way the backend's legacy pricing-tier
            // colors are no longer used.
            if (this._facilityPinConfig === FacilityPinConfig.LISTING_STATUS) {
                this._data.setFacilityPinIconImage(LISTING_STATUS_ICON_IMAGE_EXPRESSION);
            } else if (this._facilityPinConfig === FacilityPinConfig.BY_TYPE) {
                this._data.setFacilityPinIconImage(BY_TYPE_ICON_IMAGE_EXPRESSION);
            } else if (this._facilityPinConfig === FacilityPinConfig.PRICING) {
                this._data.setFacilityPinIconImage(PRICING_ICON_IMAGE_EXPRESSION);
            } else if (this._facilityPinConfig === FacilityPinConfig.LEGACY) {
                // Classic: keep the metadata's original teardrop icon-image.
                this._data.setFacilityPinIconImage(null);
            }

            if (!this._facilitiesVisible) {
                this._data.layers.forEach(layer => {
                    if (layer.layout) {
                        layer.layout.visibility = 'none';
                    }
                });
            }
        }
    };

    onApplyFilters = eventMap => {
        if (eventMap.appliedGeoFilters) {
            this._appliedFilters = eventMap.appliedGeoFilters.appliedFilters;
            this._filterCombinerType = eventMap.appliedGeoFilters.filterCombinerType;
        }
        this.applyGeoJsonLayerUpdate(eventMap);
    };

    onPointsGeoJsonLoaded = geoJson => {
        this._data.setGeoJsonSourceData(geoJson);
        // Re-merge listing flags onto freshly loaded features when the config
        // reads `on_market` or `has_listing` — both listing-status and by-type
        // need normalized `on_market` booleans for their expressions/filters.
        const needsListingFlags =
            this._facilityPinConfig === FacilityPinConfig.LISTING_STATUS ||
            this._facilityPinConfig === FacilityPinConfig.BY_TYPE;
        if (needsListingFlags) {
            this.fetchListedFacilityIds().then(listedIds => {
                this._data.mergeFeatureListingFlags(listedIds);
                // Freshly loaded features carry no derived by-type property yet. Without
                // this, any hidden legend row would filter out every new feature on the
                // next pan/zoom (they'd match no visible virtual layer).
                if (this._facilityPinConfig === FacilityPinConfig.BY_TYPE) {
                    this._data.mergeByTypeLayerIds(this.activeHighlightMetric);
                }
                this.bus.emit('GET_APPLIED_FILTERS_REQUEST');
            });
        } else {
            this.bus.emit('GET_APPLIED_FILTERS_REQUEST');
        }
    };

    /** @param {import('@turf/helpers').Feature} feature  */

    // apply the visibility change to the map
    applyGeoJSONLibraryLayersUpdate(e) {
        const { visible } = e;

        if (!this._data.sourceId || this._data.layers.length === 0) {
            return;
        }
        if (!this._isMapLoaded) {
            return;
        }

        this._data.layers.forEach(layer => {
            if (this.map.getLayer(layer.id)) {
                this.map.removeLayer(layer.id);
            }
        });

        if (!this.map.getSource(this._data.sourceId)) {
            this.map.addSource(this._data.sourceId, this._data.source);
        }

        this._data.layers.forEach(layer => {
            layer.layout.visibility = visible ? 'visible' : 'none';
            if (!this.map.getLayer(layer.id)) {
                this.map.addLayer(layer);
            }
        });
    }

    onShouldUseClustersRequest = () => {
        this.emit('SHOULD_USE_CLUSTERS_RESPONSE', { useClusters: this._shouldUseClusters });
    };

    // apply the visibility change to the map
    applyGeoJSONClusteringToggle(e) {
        const { useClusters } = e;
        this._shouldUseClusters = useClusters;
        // A facility the clusters leave unmerged is only ever drawn by the points
        // layer, so its zoom gate would hide it below the gate with clustering on.
        const { pointsLayer } = this._data;
        if (pointsLayer) {
            pointsLayer.minzoom = useClusters ? 0 : UNCLUSTERED_POINTS_MIN_ZOOM;
            if (this.map.getLayer(pointsLayer.id)) {
                this.map.setLayerZoomRange(
                    pointsLayer.id,
                    pointsLayer.minzoom,
                    pointsLayer.maxzoom,
                );
            }
        }
        if (this.map.getSource(this._data.sourceId)) {
            this.map.removeSource(this._data.sourceId);
            this._data.source.cluster = useClusters;
            this.map.addSource(this._data.sourceId, this._data.source);
        }
    }

    setLocalsFromGeoJsonLayerUpdateParams(e) {
        const {
            layerVisibility,
            featurePropertyNameEquivalentToLayerId,
            filterCombinerType,
            appliedFilters,
        } = e;
        // check if there are facility filters from legend and save them
        if (
            e.hasOwnProperty('layerVisibility') &&
            e.hasOwnProperty('featurePropertyNameEquivalentToLayerId')
        ) {
            this._layerVisibility = layerVisibility;
            this._featurePropertyNameEquivalentToLayerId = featurePropertyNameEquivalentToLayerId;
        }
        // check if there are facility filters from filter and save them
        if (e.hasOwnProperty('filterCombinerType') && e.hasOwnProperty('appliedFilters')) {
            this._filterCombinerType = filterCombinerType;
            this._appliedFilters = appliedFilters;
        }
    }

    // apply the visibility change to the map
    // it is assumed that
    //  there exists a single GeoJSON file
    //  within it there is a specific property that is used to classify features into distinct groups & display them as such
    //  dingo-maps GeoJSON support metadata defines
    //      - that property
    //      - layers created virtually from features based on classification by that property
    //      - all discrete values in classification property are names of distinct virtualized layers
    // this method determines which features get populated into the map based on classifications used
    // clusters are styled based on the number of classes active
    //  if one class is active, its style is applied to clusters
    //  if multiple classes are active, default style is applied to clusters
    applyGeoJsonLayerUpdate(e) {
        this.setLocalsFromGeoJsonLayerUpdateParams(e);

        if (
            !this._isMapLoaded ||
            !this._data.sourceId ||
            this._data.layers.length === 0 ||
            !this._data.geoJsonSourceData
        ) {
            return;
        }

        let areAllLayersHidden = false;
        // Remove layers and source
        this._data.layers.forEach(layer => {
            if (this.map.getLayer(layer.id)) {
                this.map.removeLayer(layer.id);
            }
        });
        if (this.map.getSource(this._data.sourceId)) {
            this.map.removeSource(this._data.sourceId);
        }
        const filteredSourceData = {
            type: 'FeatureCollection',
            features: [],
        };
        // Reset the cluster color
        this._data.resetClusterLayerColor();
        // if there are filters from legend, filter facilities based on those criteria
        if (this._layerVisibility && this._featurePropertyNameEquivalentToLayerId) {
            if (this._layerVisibility.every(el => el.visible)) {
                // all visible so use all the points
                this._data.resetData();
                areAllLayersHidden = false;
            } else if (this._layerVisibility.every(el => el.visible === false)) {
                areAllLayersHidden = true;
            }
            // Get the visible layer ids
            const visibleLayers = this._layerVisibility.filter(l => l.visible).map(l => l.layerId);
            // Filter the features
            filteredSourceData.features = this._data.geoJsonSourceData.features.filter(f =>
                visibleLayers.includes(f.properties[this._featurePropertyNameEquivalentToLayerId]),
            );
            // If there is only one visible layer change the color of cluster circle
            if (visibleLayers.length === 1) {
                // change the color of cluster
                const visibleLayerId = visibleLayers[0]; // there is only one
                this._data.updateClusterLayerColor(visibleLayerId);
            }
        }
        const hasAppliedFilters =
            this._appliedFilters && Object.keys(this._appliedFilters).length > 0;
        let filteredFeatures = [];
        const geoJsonSourceData = this._data.geoJsonSourceData
            ? this._data.geoJsonSourceData.features
            : [];

        // if there are filters applied and all layers visible
        if (this._filterCombinerType && hasAppliedFilters && !areAllLayersHidden) {
            const pointsFilterRules = Object.keys(this._appliedFilters).map(
                appliedFilterProperty =>
                    new PointsFilterRule({
                        property: appliedFilterProperty.replace(/[@][0-9]+/g, ''),
                        value: this._appliedFilters[appliedFilterProperty].value,
                        filterType: this._appliedFilters[appliedFilterProperty].filterType,
                    }),
            );
            // Let's create the point filter object
            const pointsFilter = new PointsFilter({
                filterCombinerType: this._filterCombinerType,
                filterRules: pointsFilterRules,
            });
            // Filter the geo json file
            filteredFeatures = pointsFilter.filter(
                filteredSourceData.features.length > 0
                    ? filteredSourceData.features
                    : geoJsonSourceData,
            );
            // if there are no applied filters but there are facilities filtered by legend filter, then show only those facilities
        } else if (!hasAppliedFilters && !areAllLayersHidden) {
            filteredFeatures =
                filteredSourceData.features.length > 0
                    ? filteredSourceData.features
                    : geoJsonSourceData;
        }
        // Set filtered source data
        filteredSourceData.features = filteredFeatures;
        this._data.setFilteredSourceData(filteredSourceData);
        if (this.map.getSource(this._data.sourceId)) {
            this.map.getSource(this._data.sourceId).setData(filteredSourceData);
        } else {
            this.map.addSource(this._data.sourceId, this._data.source);
        }

        this._data.layers.forEach(layer => {
            if (!this.map.getLayer(layer.id)) {
                this.map.addLayer(layer);
            }
        });
    }
}

export default PointsHandler;
